// The six trip actions that answer with a dispatch delta
// (DispatchDeltaResponseMixin on the backend), on @bambi/api-client's trips
// split. Cache policy like the rest of cachePolicy.ts, but it reads the
// response, so it goes through the typed split rather than the by-name
// patch table there.
import type {
  BulkTripUnassignDeltaResults,
  DispatchDelta,
} from '@bambi/api-client/schemas';
import { tripsApi } from '@bambi/api-client/trips';

import {
  COUNT_TAGS,
  type MutationCountTag,
  TRIP_MUTATION_TAGS,
} from './policyTags';
import type { RootState } from 'src/app/store/types';
import { enhancedBambiApi as legacyApi } from 'src/common/external/bambi-api/enhancedBambiApi';

/**
 * Whether the caller passed the board's filters, which is what makes the delta
 * describe a board it can be merged into. Omitting them opts out: no patch,
 * and 'dispatch' still invalidated so the board reloads the old way. The
 * multi-load assign flow relies on that (see useMultiloadAssign).
 */
type DispatchDeltaArg = { rangeStart?: string };
const isMergeable = (arg: DispatchDeltaArg) => Boolean(arg.rangeStart);

/**
 * When the response carries the delta, the dispatch cache is patched in place
 * — skip 'dispatch' so it doesn't also refetch, but keep 'trips' so trip grids
 * elsewhere stay fresh (the dispatch halves deliberately don't provide 'trips';
 * see cachePolicy.ts). The status counts are the one board-wide aggregate the
 * delta can't carry, so they refetch on their own tag. A body without an
 * `assignments` field (backend not deployed / rolled back, or a delta that
 * failed to build) falls back to today's invalidation refetch, which covers
 * the counts via 'dispatch'. Presence, not length: a delta whose only visible
 * content is an unassigned row legitimately carries `assignments: []` when the
 * calendar filters hide the vacated crew.
 */
const deltaTags = (
  arg: DispatchDeltaArg,
  delta: DispatchDelta | undefined,
  extra: readonly MutationCountTag[] = []
) => [
  ...(isMergeable(arg) && delta?.assignments
    ? (['trips', 'run-bambi-run', 'dispatch-summary'] as const)
    : TRIP_MUTATION_TAGS),
  ...extra,
];

// RTK types the store as unknown in onQueryStarted; the cast below is where
// that meets our own state type. Spelled out structurally rather than
// inferred, since one definition has to satisfy several endpoints' contexts.
type DeltaLifecycle = {
  dispatch: (action: unknown) => unknown;
  getState: () => unknown;
};

/**
 * Takes `queryFulfilled` whole, not a promise derived from it: RTK catches
 * rejections on that promise alone, so a derived one built in the argument
 * list outlives the guard below and rejects with nobody listening.
 */
const mergeDeltaWhenMergeable = async <T>(
  arg: DispatchDeltaArg,
  { dispatch, getState }: DeltaLifecycle,
  queryFulfilled: Promise<{ data: T }>,
  selectDelta: (response: T) => DispatchDelta | undefined
) => {
  if (!isMergeable(arg)) {
    return;
  }
  // Read before the await: the delta describes the board this request was
  // cut for, and the dispatcher can change date or filters while it is in
  // flight. Redux state is immutable, so this stays that board.
  const stateAtRequest = getState() as RootState;
  let response;
  try {
    response = await queryFulfilled;
  } catch {
    return; // error path reconciles via the fallback invalidation
  }
  const delta = selectDelta(response.data);
  if (!delta?.assignments) {
    return;
  }
  try {
    // Dynamic import: a static one would cycle (dispatchDelta →
    // dispatchCacheThunks → dispatcher.slice → trips hooks) and pull the
    // dispatch feature into the store bootstrap.
    const { applyDispatchDelta } =
      await import('src/features/dispatch/common/dispatchCacheThunks');
    dispatch(applyDispatchDelta(delta, stateAtRequest));
  } catch {
    // The chunk can fail to load (asset rollover mid-deploy), and by now the
    // mutation's tags already skipped 'dispatch' — invalidate it by hand so
    // the board refetches instead of going stale. The board halves are
    // legacy queries until the catch-all moves, so tell both clients.
    dispatch(tripsApi.util.invalidateTags(['dispatch']));
    dispatch(legacyApi.util.invalidateTags(['dispatch']));
  }
};

const dispatchDeltaEndpoint = (...extra: readonly MutationCountTag[]) => ({
  invalidatesTags: (
    result: DispatchDelta | undefined,
    _error: unknown,
    arg: DispatchDeltaArg
  ) => deltaTags(arg, result, extra),
  onQueryStarted: (
    arg: DispatchDeltaArg,
    {
      queryFulfilled,
      ...lifecycle
    }: DeltaLifecycle & { queryFulfilled: Promise<{ data: DispatchDelta }> }
  ) => mergeDeltaWhenMergeable(arg, lifecycle, queryFulfilled, (data) => data),
});

/**
 * Bulk unassign can decline trips and reports them, so its delta rides in the
 * body's `delta` field instead of being the body (see
 * BulkTripUnassignDeltaResultsSerializer). Same contract otherwise.
 */
const bulkUnassignDeltaEndpoint = (...extra: readonly MutationCountTag[]) => ({
  invalidatesTags: (
    result: BulkTripUnassignDeltaResults | undefined,
    _error: unknown,
    arg: DispatchDeltaArg
  ) => deltaTags(arg, result?.delta, extra),
  onQueryStarted: (
    arg: DispatchDeltaArg,
    {
      queryFulfilled,
      ...lifecycle
    }: DeltaLifecycle & {
      queryFulfilled: Promise<{ data: BulkTripUnassignDeltaResults }>;
    }
  ) =>
    mergeDeltaWhenMergeable(
      arg,
      lifecycle,
      queryFulfilled,
      (results) => results.delta
    ),
});

/**
 * Called by cachePolicy.ts before it wraps the definitions: the bridge
 * chains whatever onQueryStarted it finds, so this has to be there first.
 */
export const applyDispatchDeltaPolicy = () =>
  tripsApi.enhanceEndpoints({
    addTagTypes: COUNT_TAGS,
    endpoints: {
      tripsAssignPartialUpdate: dispatchDeltaEndpoint(
        'requested-trip-count',
        'will-call-count'
      ),
      tripsReassignPartialUpdate: dispatchDeltaEndpoint(),
      tripsBulkAssignCreate: dispatchDeltaEndpoint(
        'requested-trip-count',
        'will-call-count'
      ),
      tripsBulkReassignCreate: dispatchDeltaEndpoint(),
      tripsUnassignPartialUpdate: dispatchDeltaEndpoint('will-call-count'),
      tripsBulkUnassignCreate: bulkUnassignDeltaEndpoint('will-call-count'),
    },
  });
